import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

import { isSignedIn, useAuth } from '@/features/scaffolding/components/AuthContext';

export interface AuditLogEntry {
  id: number;
  timestamp: string;
  httpMethod: string;
  apiEndpoint: string;
  requestPayload: unknown;
  queryParams: unknown;
  responseStatus: number | null;
  responseTime: number | null;
  userId: number | null;
  orgId: number | null;
  ipAddress: string | null;
  errorMessage: string | null;
  isMcp: boolean;
  mcpSessionId: string | null;
  /** Business action a handler named on this row, e.g. `clip.delete`; null on plain request rows. */
  action: string | null;
  targetId: string | null;
  details: Record<string, unknown> | null;
  userEmail: string | null;
}

export interface AuditLogEntriesResponse {
  total: number;
  entries: AuditLogEntry[];
}

export interface AuditLogEntriesParams {
  search?: string;
  limit: number;
  offset: number;
}

/**
 * Search is a server parameter rather than a table filter: a page holds at most
 * `limit` rows, so filtering client-side would silently search only the page the
 * user happens to be on.
 */
export const useAuditLogEntriesQuery = ({ search, limit, offset }: AuditLogEntriesParams) => {
  const { sessionState } = useAuth();
  const isReady = isSignedIn(sessionState);

  return useQuery({
    queryKey: ['AuditLogEntries', search, limit, offset],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (search) params.set('search', search);

      const { data } = await get<AuditLogEntriesResponse>(
        `/v1.0/internals/audit-logs?${params.toString()}`,
      );
      return data;
    },
    enabled: isReady,
    // Paging and typing in the search box both change the key; keeping the old
    // page visible avoids the table collapsing to empty between fetches.
    placeholderData: keepPreviousData,
  });
};

export default useAuditLogEntriesQuery;
